import React, { useState } from 'react'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import toast from 'react-hot-toast'

export default function TenantPasswordTab({ tenant }) {
  const groupId = tenant?.group_id
  const [draft, setDraft] = useState({ username: '', newPassword: '', confirm: '' })
  const [saving, setSaving] = useState(false)
  const [resending, setResending] = useState(false)

  const submit = async () => {
    const username = String(draft.username || '').trim()
    if (!username) return toast.error('Username is required')
    if (!draft.newPassword || draft.newPassword.length < 8) {
      return toast.error('New password must be at least 8 characters')
    }
    if (draft.newPassword !== draft.confirm) return toast.error('Passwords do not match')
    setSaving(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${groupId}/users/set-password`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, new_password: draft.newPassword }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success(`Password updated for ${username}`)
      setDraft({ username: '', newPassword: '', confirm: '' })
    } catch (e) {
      toast.error(e.message || 'Failed')
    } finally {
      setSaving(false)
    }
  }

  const resendCredentials = async () => {
    const username = String(draft.username || '').trim()
    if (!username) return toast.error('Username is required')
    if (!confirm(`Reset password and email new login credentials to this user's email on file?`)) return
    setResending(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/admin/tenants/${groupId}/users/${encodeURIComponent(username)}/resend-credentials`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success(`Credentials emailed for ${username}`)
    } catch (e) {
      toast.error(e.message || 'Resend failed')
    } finally {
      setResending(false)
    }
  }

  if (!tenant) return null

  return (
    <div className="max-w-lg space-y-4">
      <p className="text-xs text-gray-600">
        {tenant.db_name
          ? 'Dedicated tenant DB: use the exact username from that database (gateway or mobile accounts).'
          : 'Shared database: updates the user that belongs to this GSID.'}
      </p>
      {['username', 'newPassword', 'confirm'].map((key) => (
        <label key={key} className="block text-sm">
          <span className="font-medium text-gray-700">
            {key === 'username' ? 'Username' : key === 'newPassword' ? 'New password' : 'Confirm password'}
          </span>
          <input
            type={key === 'username' ? 'text' : 'password'}
            className="mt-1 w-full rounded-md border px-3 py-2 text-sm font-mono"
            autoComplete="off"
            value={draft[key]}
            onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
          />
        </label>
      ))}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={saving || resending}
          className="rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {saving ? 'Updating…' : 'Update password only'}
        </button>
        <button
          type="button"
          onClick={resendCredentials}
          disabled={saving || resending}
          className="rounded-lg border border-psg-teal px-4 py-2 text-sm font-medium text-psg-teal disabled:opacity-50"
        >
          {resending ? 'Sending…' : 'Resend credentials (reset + email)'}
        </button>
      </div>
      <p className="text-xs text-gray-500">
        <strong>Resend credentials</strong> generates a new temporary password and emails it to the user's stored email.
        <strong> Update password only</strong> does not send email.
      </p>
    </div>
  )
}
